import React from "react";
import * as teamsJs from "@microsoft/teams-js";
import { useTranslation } from "react-i18next";
import "./styles/components/team-request-form.css";
import { apiService } from "./services/api.service";
import { HostTeamInfo, TeamTemplate, TeamPrivacyType, TeamPrivacy, AppSettings } from "./types";
import PeoplePicker, { User } from "./PeoplePicker";
import NotificationModal from "./components/NotificationModal";

interface TeamRequest {
  teamName: string;
  description: string;
  privacy: TeamPrivacy;
  justification: string;
  owner: string;
  members: string;
  templateId?: string;
  requesterId: string;
  requesterEmail: string;
  teamId: string | null;
  teamsAppId?: string; // 🔗 DEEP LINK: ID de l'application Teams pour les notifications d'approbation
}

interface TeamRequestFormProps {
  currentUserName: string;
  currentUserId: string;
  currentUserEmail: string;
  teamId: string | null;
  tenantError?: string; // ✨ MULTI-TENANT: Error message if tenant is not configured
}

export default function TeamRequestForm({ currentUserName, currentUserId, currentUserEmail, teamId, tenantError }: TeamRequestFormProps) {
  const { t } = useTranslation(['teamRequestForm', 'common']);
  const [templates, setTemplates] = React.useState<TeamTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = React.useState(false);
  const [teamInfo, setTeamInfo] = React.useState<HostTeamInfo | null>(null);
  const [loadingTeamInfo, setLoadingTeamInfo] = React.useState(false);
  const [availablePrivacyTypes, setAvailablePrivacyTypes] = React.useState<TeamPrivacyType[]>([]);
  const [teamsAppId, setTeamsAppId] = React.useState<string>("");
  const [ownerDetails, setOwnerDetails] = React.useState<{ id: string; displayName: string; email: string } | null>(null);

  const [formData, setFormData] = React.useState<TeamRequest>({
    teamName: "",
    description: "",
    privacy: "private",
    justification: "",
    owner: currentUserName,
    members: "",
    templateId: "",
    requesterId: currentUserId,
    requesterEmail: currentUserEmail,
    teamId: teamId,
    teamsAppId: "", // 🔗 DEEP LINK: ID de l'application Teams
  });

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitMessage, setSubmitMessage] = React.useState<{
    type: "success" | "error" | "warning";
    text: string;
  } | null>(null);
  const [teamNameCheck, setTeamNameCheck] = React.useState<{
    checking: boolean;
    exists: boolean;
    existingTeam: any | null;
  }>({
    checking: false,
    exists: false,
    existingTeam: null
  });

  // Update formData when user info changes
  React.useEffect(() => {
    setFormData(prev => ({
      ...prev,
      requesterId: currentUserId,
      requesterEmail: currentUserEmail,
      owner: currentUserName
    }));
  }, [currentUserId, currentUserEmail, currentUserName]);

  // Update formData.teamId when prop changes
  React.useEffect(() => {
    setFormData(prev => ({
      ...prev,
      teamId: teamId
    }));
  }, [teamId]);

  // Load templates and privacy types on component mount
  React.useEffect(() => {
    loadTemplates();
    loadPrivacyTypes();
    if (teamId) {
      loadTeamInfo();
    }
    
    // 🔗 DEEP LINK: Récupérer le Teams App ID depuis les variables d'environnement
    // ⚠️ IMPORTANT: Utiliser VITE_TEAMS_APP_ID (depuis manifest.json) et non appLaunchId
    // appLaunchId est un identifiant unique généré pour chaque lancement, pas l'ID de l'app
    const appId = import.meta.env.VITE_TEAMS_APP_ID || "";
    
    console.log("[TeamRequestForm] VITE_TEAMS_APP_ID value:", appId);
    console.log("[TeamRequestForm] import.meta.env:", import.meta.env);
    
    if (appId && appId.trim().length > 0) {
      setTeamsAppId(appId);
      
      // Mettre à jour le formData avec le teamsAppId
      setFormData(prev => ({
        ...prev,
        teamsAppId: appId
      }));
      
      console.log("[TeamRequestForm] Teams App ID from environment:", appId);
    } else {
      console.warn("[TeamRequestForm] VITE_TEAMS_APP_ID not configured or empty in environment");
      console.warn("[TeamRequestForm] Make sure TEAMS_APP_ID is set in env/.env.local");
    }
  }, [teamId]);

  // Check if team name already exists (debounced)
  React.useEffect(() => {
    const checkTeamName = async () => {
      const trimmedName = formData.teamName.trim();

      // Ne vérifier que si le nom a au moins 3 caractères
      if (trimmedName.length < 3) {
        setTeamNameCheck({ checking: false, exists: false, existingTeam: null });
        return;
      }

      setTeamNameCheck(prev => ({ ...prev, checking: true }));

      try {
        const response = await apiService.checkTeamName(trimmedName);
        if (response.success && response.data) {
          setTeamNameCheck({
            checking: false,
            exists: response.data.exists,
            existingTeam: response.data.team
          });
        }
      } catch (error) {
        console.error("Error checking team name:", error);
        setTeamNameCheck({ checking: false, exists: false, existingTeam: null });
      }
    };

    // Debounce: attendre 500ms après la dernière frappe
    const timeoutId = setTimeout(() => {
      checkTeamName();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData.teamName]);

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await apiService.getEnabledTemplates();
      if (response.success && response.data) {
        setTemplates(response.data);
      }
    } catch (error) {
      console.error("Error loading templates:", error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const loadPrivacyTypes = async () => {
    try {
      const response = await apiService.getEnabledTeamPrivacyTypes();
      if (response.success && response.data) {
        setAvailablePrivacyTypes(response.data);
        // Set default privacy to first enabled type
        if (response.data.length > 0 && !formData.privacy) {
          setFormData(prev => ({
            ...prev,
            privacy: response.data[0].id
          }));
        }
      }
    } catch (error) {
      console.error("Error loading privacy types:", error);
      // Fallback to default types if API fails
      setAvailablePrivacyTypes([
        { id: 'private', label: 'Privée', description: 'Seuls les membres invités peuvent y accéder', icon: '🔒', graphVisibility: 'private' },
        { id: 'public', label: 'Publique', description: 'Tous les membres de l\'organisation peuvent y accéder', icon: '🌐', graphVisibility: 'public' }
      ]);
    }
  };

  const loadTeamInfo = async () => {
    if (!teamId) return;
    setLoadingTeamInfo(true);
    try {
      const response = await apiService.getTeamInfo(teamId);
      if (response.success && response.data) {
        setTeamInfo(response.data);
      }
    } catch (error) {
      console.error("Error loading team info:", error);
    } finally {
      setLoadingTeamInfo(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage(null);

    try {
      // Debug logging
      console.log("Submitting request with teamId:", formData.teamId);
      console.log("Props teamId:", teamId);

      // Validation
      if (!formData.teamName.trim()) {
        throw new Error(t('teamRequestForm:validation.teamNameRequired', 'Le nom de l\'équipe est requis'));
      }
      if (!formData.description.trim()) {
        throw new Error(t('teamRequestForm:validation.descriptionRequired', 'La description est requise'));
      }
      if (!formData.justification.trim()) {
        throw new Error(t('teamRequestForm:validation.justificationRequired', 'La justification est requise'));
      }
      if (teamNameCheck.exists) {
        throw new Error(t('teamRequestForm:validation.teamNameTaken', 'Une équipe avec ce nom existe déjà. Veuillez choisir un nom différent.'));
      }

      // Préparer les données de la demande avec les détails du propriétaire
      const requestData = {
        ...formData,
        owner: ownerDetails ? [ownerDetails] : formData.owner
      };

      // Send request to backend
      const response = await apiService.submitRequest(requestData, formData.teamId);

      if (!response.success) {
        throw new Error(response.error || t('teamRequestForm:submit.error', 'Error submitting request'));
      }

      // Display success with any warnings
      let message = t('teamRequestForm:submit.success', 'Your request has been submitted successfully! It will be reviewed shortly.');
      if (response.warnings && response.warnings.length > 0) {
        message += "\n\n" + t('common:common.warning', 'Warning') + ": " + response.warnings.join("\n");
      }

      setSubmitMessage({
        type: response.warnings && response.warnings.length > 0 ? "warning" : "success",
        text: message,
      });

      // Reset form
      setFormData({
        teamName: "",
        description: "",
        privacy: "private",
        justification: "",
        owner: currentUserName,
        templateId: "",
        requesterId: currentUserId,
        requesterEmail: currentUserEmail,
        teamId: teamId,
        members: "",
      });
    } catch (error) {
      setSubmitMessage({
        type: "error",
        text: error instanceof Error ? error.message : "Une erreur est survenue",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      teamName: "",
      description: "",
      privacy: "private",
      justification: "",
      owner: currentUserName,
      templateId: "",
      requesterId: currentUserId,
      requesterEmail: currentUserEmail,
      teamId: teamId,
      members: "",
    });
    setSubmitMessage(null);
  };

  const generateRandomTestData = () => {
    const adjectives = ["Innovante", "Dynamique", "Créative", "Collaborative", "Agile", "Moderne", "Efficace", "Stratégique"];
    const nouns = ["Marketing", "Ventes", "Innovation", "Projet", "Développement", "Communication", "Recherche", "Digital"];
    const years = ["2024", "2025", "Q1", "Q2", "H1", "H2"];

    const justifications = [
      "Cette équipe est nécessaire pour améliorer la collaboration interdépartementale",
      "Nous avons besoin d'un espace dédié pour coordonner nos activités",
      "Ce projet requiert une communication centralisée et efficace",
      "Pour faciliter le partage de ressources et la gestion de projet",
      "Afin d'optimiser nos processus et notre productivité collective"
    ];

    const descriptions = [
      "Espace collaboratif dédié à la coordination des initiatives stratégiques",
      "Hub central pour partager ressources, documents et meilleures pratiques",
      "Équipe projet pour piloter nos objectifs trimestriels",
      "Plateforme de communication pour synchroniser nos efforts",
      "Centre de collaboration pour nos activités opérationnelles"
    ];

    const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)];
    const randomNoun = nouns[Math.floor(Math.random() * nouns.length)];
    const randomYear = years[Math.floor(Math.random() * years.length)];
    const randomJustification = justifications[Math.floor(Math.random() * justifications.length)];
    const randomDescription = descriptions[Math.floor(Math.random() * descriptions.length)];

    setFormData(prev => ({
      ...prev,
      teamName: `${randomAdjective} ${randomNoun} ${randomYear}`,
      description: randomDescription,
      privacy: "private",
      justification: randomJustification,
      owner: "admin@shaworktry.onmicrosoft.com"
    }));

    setSubmitMessage({
      type: "success",
      text: t('teamRequestForm:testData.success', 'Données de test générées ! Vous pouvez modifier les valeurs avant de soumettre.')
    });
  };

  return (
    <div className="fluent-form-container">
      {/* ✨ MULTI-TENANT: Warning if tenant error (for admins who can still access form) */}
      {tenantError && (
        <div className="fluent-tenant-warning">
          <div className="fluent-tenant-warning-icon">⚠️</div>
          <div>
            <strong className="fluent-tenant-warning-title">{t('teamRequestForm:tenantWarning.title', 'Configuration incomplète')}</strong>
            <div className="fluent-tenant-warning-text">
              {t('teamRequestForm:tenantWarning.message', 'L\'application n\'est pas correctement configurée. Les demandes soumises pourraient échouer.')}
              <br />
              <strong>{t('common:common.error', 'Erreur')} :</strong> {tenantError}
            </div>
          </div>
        </div>
      )}

      <NotificationModal
        isOpen={submitMessage !== null}
        type={submitMessage?.type || "success"}
        message={submitMessage?.text || ""}
        onClose={() => setSubmitMessage(null)}
      />

      <div className="fluent-card">
        <div className="fluent-card-header">
          <h2 className="fluent-title">
            <span className="fluent-icon">👥</span>
            {t('teamRequestForm:title', 'Demande de création d\'équipe Teams')}
          </h2>
        </div>

        <div className="fluent-card-content">
          <form onSubmit={handleSubmit} className="fluent-form">
            <div className="fluent-field">
              <label className="fluent-label">
                {t('teamRequestForm:fields.teamName.label', 'Nom de l\'équipe')} <span className="fluent-required">*</span>
              </label>
              <div className="fluent-input-container">
                <span className="fluent-input-icon">📄</span>
                <input
                  type="text"
                  name="teamName"
                  value={formData.teamName}
                  onChange={handleChange}
                  placeholder={t('teamRequestForm:fields.teamName.placeholder', 'Ex: Projet Marketing 2025')}
                  className="fluent-input"
                  maxLength={100}
                  required
                />
              </div>
              <div className="fluent-hint">{formData.teamName.length}/100 {t('teamRequestForm:fields.teamName.hint', 'caractères').split('/')[1]}</div>
              {teamNameCheck.checking && (
                <div className="fluent-hint fluent-hint-checking">
                  ⏳ {t('teamRequestForm:fields.teamName.checking', 'Vérification du nom d\'équipe...')}
                </div>
              )}
              {!teamNameCheck.checking && teamNameCheck.exists && (
                <div className="fluent-hint fluent-hint-error">
                  ⚠️ {t('teamRequestForm:fields.teamName.taken', 'Une équipe avec ce nom existe déjà. Veuillez choisir un nom différent.')}
                </div>
              )}
              {!teamNameCheck.checking && !teamNameCheck.exists && formData.teamName.trim().length >= 3 && (
                <div className="fluent-hint fluent-hint-success">
                  ✅ {t('teamRequestForm:fields.teamName.available', 'Ce nom est disponible')}
                </div>
              )}
            </div>

            <div className="fluent-field">
              <label className="fluent-label">
                {t('teamRequestForm:fields.description.label', 'Description')} <span className="fluent-required">*</span>
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder={t('teamRequestForm:fields.description.placeholder', 'Décrivez l\'objectif de cette équipe...')}
                className="fluent-textarea"
                rows={4}
                maxLength={500}
                required
              />
              <div className="fluent-hint">{formData.description.length}/500 {t('teamRequestForm:fields.description.hint', 'caractères').split('/')[1]}</div>
            </div>

            {templates.length > 0 && (
              <div className="fluent-field">
                <label className="fluent-label">
                  {t('teamRequestForm:fields.template.label', 'Modèle d\'équipe (optionnel)')}
                </label>
                <select
                  name="templateId"
                  value={formData.templateId}
                  onChange={handleChange}
                  className="fluent-select"
                  disabled={loadingTemplates}
                >
                  <option value="">{t('teamRequestForm:fields.template.blank', 'Équipe vierge (aucun modèle)')}</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.teamId}>
                      {template.name} - {template.description}
                    </option>
                  ))}
                </select>
                <div className="fluent-hint">
                  {t('teamRequestForm:fields.template.hint', 'Sélectionnez un modèle pour créer une équipe avec une structure prédéfinie')}
                </div>
              </div>
            )}

            <div className="fluent-field">
              <label className="fluent-label">
                {t('teamRequestForm:fields.privacy.label', 'Confidentialité')} <span className="fluent-required">*</span>
              </label>
              <select
                name="privacy"
                value={formData.privacy}
                onChange={handleChange}
                className="fluent-select"
                required
              >
                {availablePrivacyTypes.length > 0 ? (
                  availablePrivacyTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.icon} {type.label} - {type.description}
                    </option>
                  ))
                ) : (
                  <>
                    <option value="private">🔒 {t('teamRequestForm:fields.privacy.private', 'Privée')} - {t('common:common.privateTeamDescription', 'Seuls les membres invités peuvent y accéder')}</option>
                    <option value="public">🌐 {t('teamRequestForm:fields.privacy.public', 'Publique')} - {t('common:common.publicTeamDescription', 'Tous les membres de l\'organisation peuvent y accéder')}</option>
                  </>
                )}
              </select>
            </div>

            <div className="fluent-field">
              <label className="fluent-label">
                {t('teamRequestForm:fields.owner.label', 'Propriétaire de l\'équipe')} <span className="fluent-required">*</span>
              </label>
              <PeoplePicker
                selectedUser={formData.owner}
                onUserSelect={(user: User) => {
                  setFormData((prev) => ({
                    ...prev,
                    owner: user.displayName,
                  }));
                  // Capturer les détails du propriétaire
                  setOwnerDetails({
                    id: user.id,
                    displayName: user.displayName,
                    email: user.mail || user.userPrincipalName || ""
                  });
                }}
                placeholder={t('teamRequestForm:fields.owner.placeholder', 'Rechercher un propriétaire...')}
                required
              />
              <div className="fluent-hint">{t('teamRequestForm:fields.owner.hint', 'La personne responsable de l\'équipe')}</div>
            </div>

            <div className="fluent-field">
              <label className="fluent-label">
                {t('teamRequestForm:fields.justification.label', 'Justification')} <span className="fluent-required">*</span>
              </label>
              <div className="fluent-textarea-container">
                <span className="fluent-textarea-icon">❓</span>
                <textarea
                  name="justification"
                  value={formData.justification}
                  onChange={handleChange}
                  placeholder={t('teamRequestForm:fields.justification.placeholder', 'Pourquoi cette équipe est-elle nécessaire ?')}
                  className="fluent-textarea"
                  rows={4}
                  maxLength={1000}
                  required
                />
              </div>
              <div className="fluent-hint">{formData.justification.length}/1000 {t('teamRequestForm:fields.justification.hint', 'caractères').split('/')[1]}</div>
            </div>

            <div className="fluent-button-group">
              <button
                type="submit"
                className="fluent-button fluent-button-primary fluent-button-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="fluent-button-spinner"></span>
                    {t('teamRequestForm:submit.submitting', 'Envoi en cours...')}
                  </>
                ) : (
                  t('teamRequestForm:submit.button', 'Soumettre la demande')
                )}
              </button>
              <button
                type="button"
                className="fluent-button fluent-button-secondary"
                onClick={resetForm}
                disabled={isSubmitting}
              >
                {t('teamRequestForm:buttons.reset', 'Réinitialiser')}
              </button>
              <button
                type="button"
                className="fluent-button fluent-button-secondary"
                onClick={generateRandomTestData}
                disabled={isSubmitting}
                title={t('teamRequestForm:testData.button', 'Génère automatiquement des données de test')}
              >
                {t('teamRequestForm:buttons.test', '🎲 Test')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
